import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { getForms, deleteForm, submitFormForApproval, publishForm } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, Send, ExternalLink, Eye, LayoutTemplate, FileText, Loader2, Copy } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const Forms = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isSuperAdmin, canApprove } = useAuth();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('__all__');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Template selection state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  useEffect(() => {
    loadForms();
  }, [statusFilter]);

  const loadForms = async () => {
    try {
      const status = statusFilter === '__all__' ? null : statusFilter;
      const data = await getForms(status);
      setForms(data);
    } catch (error) {
      toast.error('Fehler beim Laden der Formulare');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/form-templates`, { headers: getAuthHeader() });
      setTemplates(res.data || []);
    } catch (error) {
      // Templates might not be available
    } finally {
      setTemplatesLoading(false);
    }
  };

  const openCreateDialog = () => {
    loadTemplates();
    setCreateDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Formular wirklich löschen?')) return;
    try {
      await deleteForm(id);
      toast.success('Formular gelöscht');
      loadForms();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleSubmitForApproval = async (id) => {
    try {
      const response = await submitFormForApproval(id);
      
      // Check if it was a direct publish (role override)
      if (response.requires_approval === false) {
        toast.success(response.message || 'Formular direkt veröffentlicht (Rollen-Override)');
      } else {
        toast.success('Formular zur Genehmigung eingereicht');
      }
      loadForms();
    } catch (error) {
      toast.error('Fehler beim Einreichen');
    }
  };

  const handleDirectPublish = async (id) => {
    try {
      const res = await axios.post(`${API_URL}/api/forms/${id}/direct-publish`, {}, { headers: getAuthHeader() });
      toast.success(res.data.message || 'Formular direkt veröffentlicht');
      loadForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Veröffentlichen');
    }
  };

  // Check if user can directly publish forms (Super Admin, Dept Head, Deputy)
  const canDirectPublish = (form) => {
    if (!user) return false;
    
    // Super Admin can publish any form
    if (user.role === 'super_admin') return true;
    
    // Department Head and Deputy can publish their own department's forms
    if (['department_head', 'deputy'].includes(user.role)) {
      return form.department_id === user.department_id;
    }
    
    return false;
  };

  const handlePublish = async (id) => {
    try {
      await publishForm(id);
      toast.success('Formular veröffentlicht');
      loadForms();
    } catch (error) {
      toast.error('Fehler beim Veröffentlichen');
    }
  };

  const handleDuplicate = async (formId) => {
    try {
      await axios.post(`${API_URL}/api/forms/${formId}/duplicate`, {}, { headers: getAuthHeader() });
      toast.success('Formular dupliziert');
      loadForms();
    } catch (error) {
      toast.error('Fehler beim Duplizieren');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      draft: { label: 'Entwurf', className: 'bg-gray-200 text-gray-800' },
      pending_approval: { label: 'Ausstehend', className: 'bg-orange-200 text-orange-800' },
      approved: { label: 'Genehmigt', className: 'bg-green-200 text-green-800' },
      rejected: { label: 'Abgelehnt', className: 'bg-red-200 text-red-800' },
      live: { label: 'Live', className: 'bg-blue-200 text-blue-800' },
      archived: { label: 'Archiviert', className: 'bg-gray-400 text-gray-700' }
    };
    const variant = variants[status] || variants.draft;
    return <Badge className={variant.className}>{variant.label}</Badge>;
  };

  const filteredForms = forms.filter(form =>
    form.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    form.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64">
      <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
    </div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('forms.title')}</h1>
          <p className="text-gray-500 mt-1">Verwalten Sie Ihre Formulare</p>
        </div>
        <Button onClick={openCreateDialog}>
          <Plus className="h-4 w-4 mr-2" />
          {t('forms.newForm')}
        </Button>
      </div>

      {/* Create Form Dialog with Template Selection */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('forms.newForm')}</DialogTitle>
            <DialogDescription>Wählen Sie, wie Sie Ihr Formular erstellen möchten</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="grid grid-cols-2 gap-4">
              <Card 
                className="cursor-pointer hover:border-blue-500 hover:shadow-md transition-all"
                onClick={() => { setCreateDialogOpen(false); navigate('/admin/forms/new'); }}
              >
                <CardContent className="pt-6 text-center">
                  <FileText className="h-12 w-12 mx-auto text-slate-400 mb-3" />
                  <h3 className="font-semibold">{t('forms.startEmpty')}</h3>
                  <p className="text-sm text-slate-500 mt-1">Mit einem leeren Formular beginnen</p>
                </CardContent>
              </Card>
              <Card className="border-dashed">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <LayoutTemplate className="h-5 w-5" />
                    {t('forms.fromTemplate')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {templatesLoading ? (
                    <div className="py-4 text-center">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
                    </div>
                  ) : templates.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">Keine Vorlagen verfügbar</p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {templates.map(template => (
                        <div
                          key={template.id}
                          className="p-2 border rounded hover:bg-slate-50 cursor-pointer flex items-center justify-between"
                          onClick={() => { setCreateDialogOpen(false); navigate(`/admin/forms/new?template=${template.id}`); }}
                        >
                          <div>
                            <p className="font-medium text-sm">{template.name}</p>
                            <p className="text-xs text-slate-500">{template.description?.slice(0, 50) || 'Keine Beschreibung'}</p>
                          </div>
                          <Badge variant="outline">{template.category}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>{t('common.cancel')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4 mb-6">
            <Input
              placeholder="Formulare suchen..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-sm"
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Alle Status</SelectItem>
                <SelectItem value="draft">Entwurf</SelectItem>
                <SelectItem value="pending_approval">Ausstehend</SelectItem>
                <SelectItem value="approved">Genehmigt</SelectItem>
                <SelectItem value="live">Live</SelectItem>
                <SelectItem value="rejected">Abgelehnt</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>{t('common.status')}</TableHead>
                <TableHead>{t('departments.title')}</TableHead>
                <TableHead>Ersteller</TableHead>
                <TableHead>Einreichungen</TableHead>
                <TableHead className="text-right">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredForms.map((form) => (
                <TableRow key={form.id}>
                  <TableCell className="font-medium">{form.name}</TableCell>
                  <TableCell>{getStatusBadge(form.status)}</TableCell>
                  <TableCell>{form.department_name}</TableCell>
                  <TableCell>{form.created_by_name}</TableCell>
                  <TableCell>{form.submission_count}</TableCell>
                  <TableCell>
                    <div className="flex gap-1 justify-end">
                      {form.status === 'live' && (
                        <>
                          {form.shortlink ? (
                            <>
                              <Link to={`/f/${form.shortlink}`} target="_blank">
                                <Button size="sm" variant="ghost" title="Formular öffnen">
                                  <ExternalLink className="h-4 w-4" />
                                </Button>
                              </Link>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                title="Link kopieren"
                                onClick={() => {
                                  const shortUrl = `${window.location.origin}/f/${form.shortlink}`;
                                  navigator.clipboard.writeText(shortUrl);
                                  toast.success('Link kopiert!');
                                }}
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                            </>
                          ) : (
                            <Link to={`/form/${form.slug}`} target="_blank">
                              <Button size="sm" variant="ghost" title="Formular öffnen">
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            </Link>
                          )}
                        </>
                      )}
                      {(form.status === 'draft' || form.status === 'rejected') && canDirectPublish(form) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDirectPublish(form.id)}
                          className="text-green-600"
                          title="Direkt veröffentlichen (Rollen-Override)"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                      {(form.status === 'draft' || form.status === 'rejected') && !canDirectPublish(form) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleSubmitForApproval(form.id)}
                          title="Zur Genehmigung einreichen"
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                      )}
                      {form.status === 'pending_approval' && canDirectPublish(form) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDirectPublish(form.id)}
                          className="text-green-600"
                          title="Direkt veröffentlichen (Rollen-Override)"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                      {form.status === 'approved' && canApprove() && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handlePublish(form.id)}
                          className="text-green-600"
                          title="Veröffentlichen"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                      <Link to={`/admin/forms/${form.id}/edit`}>
                        <Button size="sm" variant="ghost">
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </Link>
                      <Button size="sm" variant="ghost" onClick={() => handleDuplicate(form.id)} title={t('common.duplicate')}>
                        <Copy className="h-4 w-4" />
                      </Button>
                      {(form.status === 'draft' || canApprove()) && (
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(form.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Forms;
